/**
 * What "Save as a new version" sends, and a change the memo chat proposed
 * saved straight to one.
 *
 * Applying a proposal is the person's decision to change the memo, so it
 * does not pass through edit mode: the change is played over the version on
 * screen exactly as a person's own edit would be, and saved as the deal's
 * next version. The version it was applied to stays as it was, so the change
 * can always be compared against it, or taken back by restoring it.
 *
 * Both roads build the same payload here, so a change applied from the chat
 * is stored as nothing other than an edit.
 */

import type { JSONContent } from "@tiptap/react";

import { editSections, startMemoDoc } from "../../hooks/useMemoEditDraft";
import type {
	DealScreeningRun,
	DealScreeningSaveVersionPayload,
} from "../../types/dealScreening";
import { EMPTY_FIGURES, type FiguresDraft, figuresPayload } from "./figuresEditModel";
import {
	type AddedSection,
	isEditableSection,
	itemsFromDoc,
	itemsKey,
	sectionChangesOf,
	splitMemoDoc,
	titlesOf,
	withSectionDoc,
} from "./memoEditorModel";
import { type PlacedEdit, type ProposalContext, applyEdit } from "./memoEditProposal";
import { type EditItem, opsOf } from "./sectionEditModel";

type VersionEdits = DealScreeningSaveVersionPayload["edits"];

/**
 * Each changed section's whole edit script. A section typed in re-sends its
 * script; one only renamed (the financial summary, say) sends the rename.
 */
export function versionEdits(
	run: DealScreeningRun,
	itemsBySection: Record<string, EditItem[]>,
	sectionKeys: string[],
	titles: Record<string, string>,
): VersionEdits {
	return sectionKeys.flatMap((key) => {
		const section = run.sections.find((s) => s.key === key);
		if (!section) return [];
		const title = titles[key]?.trim() || section.title;
		const ops = itemsBySection[key]
			? opsOf(section, itemsBySection[key], title)
			: [{ op: "title" as const, title }];
		return [{ section_key: key, ops, base_body_sha: section.base_body_sha ?? "" }];
	});
}

/** Sections added in a draft and the person's own taken out, as the save sends them (DOP-656). */
export function sectionsPayload(
	added: AddedSection[],
	removed: string[],
): Pick<DealScreeningSaveVersionPayload, "new_sections" | "removed_sections"> {
	return {
		...(added.length > 0
			? {
					new_sections: added.map((section) => ({
						title: section.title,
						after: section.after,
						ops: opsOf(section.section, section.items),
					})),
				}
			: {}),
		...(removed.length > 0 ? { removed_sections: removed } : {}),
	};
}

/** What an edit draft changes, as the save sends it. */
export function draftPayload(
	run: DealScreeningRun,
	start: JSONContent,
	doc: JSONContent,
	figures: FiguresDraft,
): Omit<DealScreeningSaveVersionPayload, "name"> {
	const sections = editSections(run).filter(isEditableSection);
	const startParts = splitMemoDoc(start);
	const parts = splitMemoDoc(doc);
	const items: Record<string, EditItem[]> = {};
	const changed: string[] = [];
	sections.forEach((section) => {
		const was = itemsFromDoc(section, startParts[section.key] ?? { type: "doc" });
		items[section.key] = itemsFromDoc(section, parts[section.key] ?? { type: "doc" });
		if (itemsKey(was) !== itemsKey(items[section.key])) changed.push(section.key);
	});
	const startTitles = titlesOf(start);
	const titles = titlesOf(doc);
	const renamed = Object.keys(titles).filter(
		(key) => titles[key].trim() !== "" && titles[key].trim() !== (startTitles[key] ?? "").trim(),
	);
	const { added, removed } = sectionChangesOf(run, doc);
	return {
		edits: versionEdits(run, items, Array.from(new Set([...changed, ...renamed])), titles),
		...figuresPayload(figures),
		...sectionsPayload(added, removed),
	};
}

/** The name a version saved from the chat is given: what it changed. */
export function proposalVersionName(changes: PlacedEdit[]): string {
	const name =
		changes.length === 1
			? `From chat: ${changes[0].title}, ${changes[0].summary}`
			: `From chat: ${changes.length} changes`;
	return name.slice(0, 255);
}

/**
 * The save for changes the memo chat proposed, applied to the version on
 * screen; null when they change nothing of it.
 */
export function proposalVersion(
	run: DealScreeningRun,
	numberOf: (anchor: string | null | undefined) => number | null,
	context: ProposalContext,
	changes: PlacedEdit[],
): DealScreeningSaveVersionPayload | null {
	const start = startMemoDoc(run, numberOf);
	const applied = changes.reduce(
		(draft, placed) =>
			applyEdit(context, draft, placed, (doc, key) => splitMemoDoc(doc)[key], withSectionDoc),
		{ doc: start, figures: EMPTY_FIGURES },
	);
	const payload = draftPayload(run, start, applied.doc, applied.figures);
	if (payload.edits.length === 0 && Object.keys(payload).length === 1) return null;
	return { name: proposalVersionName(changes), ...payload };
}

/** Where a change lands in the memo, to bring it into view. */
export function changedSectionKey(change: PlacedEdit): string | null {
	if (change.kind === "figures") return "financial_summary";
	if (change.kind === "company_facts" || change.kind === "section") return null;
	return change.section.key;
}

// ------------------------------------------------------------------ what was applied

/** A proposal that was applied, and the version it was saved as. */
export interface AppliedProposal {
	version: number;
	runId: string;
}

/**
 * Whether a proposal applied before reads as applied on the version on
 * screen. It does on the version it was saved as, and on any that has the
 * change or no place left for it. A version without the change (an earlier
 * one, or one restored from before it) can have it applied.
 */
export function appliedHere(
	applied: AppliedProposal,
	run: DealScreeningRun,
	numberOf: (anchor: string | null | undefined) => number | null,
	context: ProposalContext,
	placed: PlacedEdit | null,
): boolean {
	if (applied.runId === run.public_id) return true;
	// No place left for it: on the version it was saved as or a later one,
	// that is because it went in (the paragraph it rewrote reads differently
	// now). On an earlier version it says nothing about this version, and
	// the card shows why it cannot be placed instead.
	if (!placed || placed.problem) return run.version >= applied.version;
	return proposalVersion(run, numberOf, context, [placed]) === null;
}

const appliedKey = (dealId: string) => `doppler.dealScreening.appliedProposals.${dealId}`;

/**
 * A block as its own name: the same block whether it is read from the
 * answer's text or from what the chat rendered (which escapes a `$` before
 * a digit), and whatever its spacing.
 */
export function blockKey(block: string): string {
	const bare = block.replace(/\\\$/g, "$").replace(/\s+/g, "");
	let hash = 5381;
	for (let i = 0; i < bare.length; i += 1) hash = (hash * 33 + bare.charCodeAt(i)) >>> 0;
	return `${bare.length}.${hash.toString(36)}`;
}

/**
 * The proposals applied to a deal's memo, kept on this computer so a card
 * still says it was applied after a reload. Reading and writing never
 * throws: without storage a card offers Apply again, and applying a change
 * twice only finds nothing left to change.
 */
export function readApplied(dealId: string): Record<string, AppliedProposal> {
	try {
		const raw = window.localStorage.getItem(appliedKey(dealId));
		const parsed = raw ? (JSON.parse(raw) as unknown) : null;
		return parsed && typeof parsed === "object" ? (parsed as Record<string, AppliedProposal>) : {};
	} catch {
		return {};
	}
}

export function writeApplied(
	dealId: string,
	keys: string[],
	applied: AppliedProposal,
): Record<string, AppliedProposal> {
	const next = { ...readApplied(dealId) };
	keys.forEach((key) => {
		next[key] = applied;
	});
	try {
		window.localStorage.setItem(appliedKey(dealId), JSON.stringify(next));
	} catch {
		// Storage blocked: the cards know for as long as the page is open.
	}
	return next;
}
